import { Fragment, useEffect, useRef, useState } from 'react';
// Import last
import cx from 'classnames';
import { BrowserName, getBrowserName } from '../../browser-name';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { isTruncated } from '../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Button, ButtonVariant } from '../Button';
import { useDensity } from '../Density';
import { BaseInput } from '../Input/__internal__/BaseInput';
import { InvisibleText } from '../InvisibleText';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { useRequiredValidationAdapter } from './__internal__/hooks/useRequiredValidationAdapter';
import bundle from './NovaComponents-CompositeInput-gui-icu.properties';
import cn from './CompositeInput.css.json';
import './CompositeInput.css';
import { useIconA11yInfo } from '../Icon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useIconId = createUseHtmlId('CompositeInput-icon');
const useCompositeButtonId = createUseHtmlId('CompositeInput-button');
const useTextLocalizer = createUseTextLocalizer(bundle);
const noop = () => {};
const modeInputProps = {
  select: {
    editable: false,
    variant: 'composite'
  },
  combo: {
    variant: 'composite',
    editable: true
  },
  readonly: {
    editable: false
  }
};
export const CompositeInput = props => {
  const localize = useTextLocalizer();
  const internalInputRef = useRef(null);
  const {
    value,
    mode = 'select',
    disabled,
    style,
    commitOnEnterInDialog,
    entryFieldProps,
    entryFieldRef,
    onOpen,
    buttonDisplay,
    buttonProps,
    buttonRef,
    testId,
    ariaLabel,
    ariaLabelledBy,
    ariaRoleDescription,
    ariaDescribedBy,
    className,
    isSelectorOpen,
    id,
    iconProps,
    ...restOfProps // For things like style and className
  } = props;
  const readonly = mode === 'readonly';
  const btnRef = useRef(null);
  const {
    keyboardTooltip: entryFieldKeyboardTooltip,
    getKeyboardTooltip: getEntryFieldKeyboardTooltip,
    ...restEntryFieldProps
  } = entryFieldProps ?? {};
  const iconId = iconProps?.id ?? useIconId();
  const getIconA11yInfo = useIconA11yInfo();
  const iconTooltip = iconProps ? iconProps.tooltip ?? getIconA11yInfo(iconProps).tooltip : '';
  const iconAriaLabel = iconProps?.ariaLabel ?? iconTooltip;
  const getKeyboardTooltip = () => {
    const keyboardTooltips = [];
    const tooltips = [];
    if (iconProps?.icon && !!iconTooltip && props.value && mode !== 'combo') {
      tooltips.push(iconTooltip);
      tooltips.push(`, ${props.value}`);
      if (props.entryFieldProps?.customTooltip) {
        tooltips.push('\n');
        tooltips.push(props.entryFieldProps?.customTooltip);
      }
      keyboardTooltips.push(tooltips.join(''));
      if (mode === 'select') keyboardTooltips.push(localize(`${mode}.mode.keyboard.tooltip.label`));
      if (props.entryFieldProps?.customTooltip) {
        tooltips.push(props.entryFieldProps.customTooltip);
      }
      return keyboardTooltips.join('\n\n');
    }
    if (entryFieldKeyboardTooltip) {
      return entryFieldKeyboardTooltip;
    }
    if (getEntryFieldKeyboardTooltip) {
      return getEntryFieldKeyboardTooltip();
    }
    if (props.value === '' && internalInputRef.current && props.entryFieldProps?.placeholder && isTruncated(internalInputRef.current, props.entryFieldProps.placeholder)) {
      tooltips.push(props.entryFieldProps.placeholder);
    }
    if (internalInputRef.current && props.value && isTruncated(internalInputRef.current)) {
      tooltips.push(value);
    }
    if (props.entryFieldProps?.customTooltip) {
      tooltips.push(props.entryFieldProps.customTooltip);
    }
    if (tooltips.length > 0) {
      keyboardTooltips.push(tooltips.join('\n'));
    }
    if (mode === 'select' || mode === 'combo') {
      keyboardTooltips.push(localize(`${mode}.mode.keyboard.tooltip.label`));
    }
    return keyboardTooltips.join('\n\n');
  };
  const computedAriaDescribedBy = [];
  if (Array.isArray(ariaDescribedBy) && ariaDescribedBy.join('').length > 0) {
    computedAriaDescribedBy.push(...ariaDescribedBy);
  } else if (typeof ariaDescribedBy === 'string' && ariaDescribedBy.length > 0) {
    computedAriaDescribedBy.push(ariaDescribedBy);
  }
  const computedInputProps = {
    onChange: noop,
    // let entryFieldProps override if it has it
    ...restEntryFieldProps,
    enableAutoComplete: false,
    value,
    disabled,
    commitOnEnterInDialog: commitOnEnterInDialog || mode === 'select',
    className: cx(cn.input, restEntryFieldProps?.className),
    ...modeInputProps[mode],
    isReadOnlyContainer: props.entryFieldProps?.isReadOnlyContainer
  };
  const compositeButtonLabelId = useCompositeButtonId();
  // The aria labelledby computations for the composite button
  const computedButtonAriaLabelledBy = [compositeButtonLabelId];
  if (!!props.ariaLabelledBy) {
    computedButtonAriaLabelledBy.splice(0, 0, props.ariaLabelledBy); // Prepend the composite input field label
  }

  const computedButtonProps = {
    tooltip: typeof buttonDisplay !== 'string' ? localize('button.default.tooltip.label') : undefined,
    ...buttonProps,
    ariaLabelledBy: computedButtonAriaLabelledBy.join(' '),
    text: typeof buttonDisplay === 'string' ? buttonDisplay : undefined,
    icon: typeof buttonDisplay !== 'string' ? {
      icon: buttonDisplay
    } : undefined,
    disabled,
    className: cx(cn.button, mode !== 'select' && mode !== 'readonly' && cn.editableButton, readonly && cn.readonlyButton, buttonProps?.className),
    onPress: onOpen,
    onBlur: e => {
      // When we focus out of the composite button, we call the required validator's onBlur to trigger the validation
      if (e.target === btnRef.current && e.relatedTarget !== internalInputRef.current) restRequiredValidationProps.onBlur();
    }
  };
  const altArrowPressed = useRef(false);
  const openForSelectModeRef = useRef(false);
  // Trigger onOpen on certain keyboard interactions.
  // It would be nicer if BaseInput provided support for this scenario directly but this
  // is the quickest way to get where we need to.
  const preventOpenViaKeyboard = readonly || disabled;
  /**
   * The default validations should not trigger when :
   *
   * 1. focus is inside CompositeInput
   * 2. CompositeInput button is clicked the first time when the page renders
   *
   */
  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    if (preventOpenViaKeyboard) return;
    const handler = e => {
      const altArrowPress = e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown');
      altArrowPressed.current = altArrowPress;
      const openForSelectMode = mode === 'select' && (e.key === 'Enter' || altArrowPress);
      openForSelectModeRef.current = openForSelectMode;
      const openForComboMode = mode === 'combo' && altArrowPress;
      if (openForSelectMode || openForComboMode) onOpen?.();
    };
    internalInputRef?.current?.addEventListener('keyup', handler);
    return () => {
      internalInputRef?.current?.removeEventListener('keyup', handler);
    };
  }, [mode, preventOpenViaKeyboard, onOpen]);
  const isBtnPressed = useRef(false);
  const requiredValidationProps = useRequiredValidationAdapter({
    value,
    inputProps: props.entryFieldProps
  });
  const {
    errorState,
    ...restRequiredValidationProps
  } = requiredValidationProps;
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';
  const tokens = getCompositeInputDesignTokens(dt, {
    isCustomFocusTurnedOn,
    disabled: props.disabled ?? false,
    readonly
  });
  const clearButtonRef = useRef(null);
  const setClearButtonRef = buttonElement => {
    if (buttonElement) {
      setRef(clearButtonRef, buttonElement);
      setRef(restEntryFieldProps?.clearButtonRef, buttonElement);
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": testId,
    className: cx(cn.container, disabled && cn.disabled, errorState?.status === 'alert' && cn.alert, errorState?.status === 'warning' && cn.warning, className),
    style: {
      ...tokens,
      ...props.style
    },
    role: "group",
    "aria-roledescription": ariaRoleDescription,
    ...restOfProps,
    /**
     * Preventing input validations unless the focus moves out of the composite Input field. onBlurCapture and
     * onFocusCapture help us prevent passing another prop to BaseInput for this issue.
     */
    onBlurCapture: e => {
      if (isBtnPressed.current && !e.currentTarget.contains(e.relatedTarget)) {
        e.stopPropagation();
        isBtnPressed.current = false;
      }
      if (e.target === internalInputRef.current && e.relatedTarget === btnRef.current || altArrowPressed.current || openForSelectModeRef.current) {
        setIsDirty(false);
        altArrowPressed.current = false;
        openForSelectModeRef.current = false;
        /*
          For some reason Firefox doesn't left-align the text once focus moves out of the input field.
          So we need to manually set the selectionRange at the start of the string when the onBlur event is triggered.
        */
        const browserName = getBrowserName();
        browserName === BrowserName.Firefox && internalInputRef.current?.setSelectionRange(0, 0, undefined);
        props.entryFieldProps?.onCommit?.({
          value: props.value,
          reason: 'blur'
        });
        e.stopPropagation();
      } else {
        setIsDirty(true);
      }
    },
    onFocusCapture: e => {
      if (e.target === btnRef.current && e.relatedTarget === null) {
        setIsDirty(false);
      } else {
        setIsDirty(true);
        if (e.target === btnRef.current && e.relatedTarget !== internalInputRef.current && isDirty || altArrowPressed.current) {
          altArrowPressed.current = false;
          openForSelectModeRef.current = false;
        }
      }
    },
    children: [/*#__PURE__*/_jsx(BaseInput, {
      id: id,
      hideBorder: true,
      ...(iconProps?.icon && mode !== 'combo' && {
        iconProps: {
          id: iconId,
          disabled: props.disabled,
          ariaLabel: iconAriaLabel,
          ...iconProps
        }
      }),
      futureTheme: () => ({
        '--input-bg': 'transparent'
      }),
      ...computedInputProps,
      ...restRequiredValidationProps,
      ...(errorState && {
        message: errorState.message,
        status: errorState.status
      }),
      title: props.entryFieldProps?.customTooltip,
      customTooltip: props.entryFieldProps?.customTooltip,
      ariaLabel: ariaLabel,
      ariaLabelledBy: ariaLabelledBy,
      ariaDescribedBy: computedAriaDescribedBy,
      testId: postfixTestId(testId, 'input'),
      getKeyboardTooltip: getKeyboardTooltip,
      clearButtonRef: setClearButtonRef,
      ref: e => {
        internalInputRef.current = e;
        setRef(entryFieldRef, e);
      }
      // match height with other input field when using density high
      ,
      ...(density === 'high' && {
        style: {
          '--input-min-height': 'calc(1.5rem - 2px)'
        }
      })
    }), /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        variant: ButtonVariant.Light,
        ...computedButtonProps,
        testId: postfixTestId(testId, 'button'),
        onPress: e => {
          isBtnPressed.current = true;
          computedButtonProps.onPress?.(e);
        },
        ref: e => {
          setRef(buttonRef, e);
          btnRef.current = e;
        }
      }), /*#__PURE__*/_jsx(InvisibleText, {
        id: compositeButtonLabelId,
        children: typeof buttonDisplay === 'string' ? buttonDisplay : buttonProps?.ariaLabel ?? buttonProps?.tooltip ?? localize('button.default.tooltip.label')
      })]
    })]
  });
};
export const getCompositeInputDesignTokens = (dt, opts) => {
  // Outline Focus Indicator
  let outline;
  let outlineOffset;
  if (dt.themeVariant !== 'high-contrast' && !opts?.isCustomFocusTurnedOn && !opts?.disabled && !opts?.readonly) {
    outline = 'none';
  } else {
    outline = 'var(--focus-light-bg)';
    outlineOffset = 'calc(-1 * var(--focus-width))';
  }
  let tokens = {
    bd: dt.tokens['color-action-border-bottom-control-normal'],
    bg: dt.tokens['color-action-background-control-normal'],
    fg: dt.tokens['color-action-text-control-normal'],
    'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
    'hover-bg': dt.tokens['color-action-background-control-hover'],
    'hover-fg': dt.tokens['color-action-text-control-hover'],
    'active-bd': dt.tokens['color-action-border-bottom-control-selected'],
    'active-bg': dt.tokens['color-action-background-control-selected'],
    'active-fg': dt.tokens['color-action-text-control-selected']
  };
  if (opts?.readonly) {
    tokens = {
      bd: dt.tokens['color-action-border-control-read-only'],
      bg: dt.tokens['color-action-background-control-read-only'],
      fg: dt.tokens['color-action-text-control-normal'],
      'hover-bd': 'var(--composite-input-bd)',
      'hover-bg': 'var(--composite-input-bg)',
      'hover-fg': 'var(--composite-input-fg)',
      'active-bd': 'var(--composite-input-bd)',
      'active-bg': 'var(--composite-input-bg)',
      'active-fg': 'var(--composite-input-fg)'
    };
  }
  if (opts?.disabled) {
    tokens = {
      fg: dt.tokens['color-action-text-control-disabled'],
      bd: opts?.readonly ? dt.tokens['color-action-border-control-read-only'] : dt.tokens['color-action-border-control-disabled'],
      bg: dt.tokens['color-action-background-control-disabled'],
      'hover-bd': 'var(--composite-input-bd)',
      'hover-bg': 'var(--composite-input-bg)',
      'hover-fg': 'var(--composite-input-fg)',
      'active-bd': 'var(--composite-input-bd)',
      'active-bg': 'var(--composite-input-bg)',
      'active-fg': 'var(--composite-input-fg)'
    };
  }
  return {
    ...renderDesignTokens({
      ...tokens,
      'bd-width': `0 0 ${dt.tokens['size-border-width-s']} 0`,
      'focus-outline': outline,
      'focus-outline-offset': outlineOffset,
      'error-bd': dt.tokens['color-action-border-bottom-control-alert'],
      'warning-bd': dt.tokens['color-action-border-bottom-control-warning'],
      'error-bg': dt.tokens['color-action-background-control-alert'],
      'warning-bg': dt.tokens['color-action-background-control-warning'],
      'non-editable-bg': 'transparent',
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
    }, 'composite-input')
  };
};
//# sourceMappingURL=CompositeInput.js.map